"use client"

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"
import type { ConversationTurn } from "@/lib/types"
import type { BrowserAnalysis } from "@/lib/browser-analysis"

interface ConversationState {
  file: File | null
  turns: ConversationTurn[]
  analysis: BrowserAnalysis | null
  setFile: (f: File | null) => void
  setTurns: (t: ConversationTurn[]) => void
  setAnalysis: (a: BrowserAnalysis | null) => void
  clear: () => void
}

const ConversationContext = createContext<ConversationState | null>(null)

function loadAnalysisFromSession(): BrowserAnalysis | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem("lumensis_browser_analysis")
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveAnalysisToSession(analysis: BrowserAnalysis | null) {
  if (typeof window === "undefined") return
  if (analysis) {
    sessionStorage.setItem("lumensis_browser_analysis", JSON.stringify(analysis))
  } else {
    sessionStorage.removeItem("lumensis_browser_analysis")
  }
}

export function ConversationProvider({ children }: { children: ReactNode }) {
  const [file, setFileState] = useState<File | null>(null)
  const [turns, setTurnsState] = useState<ConversationTurn[]>([])
  const [analysis, setAnalysisState] = useState<BrowserAnalysis | null>(null)

  // Defer sessionStorage read to after mount (avoids hydration mismatch)
  useEffect(() => {
    const stored = loadAnalysisFromSession()
    if (stored) setAnalysisState(stored)
  }, [])

  const setFile = useCallback((f: File | null) => setFileState(f), [])
  const setTurns = useCallback((t: ConversationTurn[]) => setTurnsState(t), [])
  const setAnalysis = useCallback((a: BrowserAnalysis | null) => {
    setAnalysisState(a)
    saveAnalysisToSession(a)
  }, [])

  const clear = useCallback(() => {
    setFileState(null)
    setTurnsState([])
    setAnalysisState(null)
    saveAnalysisToSession(null)
  }, [])

  return (
    <ConversationContext.Provider value={{ file, turns, analysis, setFile, setTurns, setAnalysis, clear }}>
      {children}
    </ConversationContext.Provider>
  )
}

export function useConversation(): ConversationState {
  const ctx = useContext(ConversationContext)
  if (!ctx) throw new Error("useConversation must be used within ConversationProvider")
  return ctx
}
